import { Box } from "@mui/material";
import { useRef, type ReactNode } from "react";
import { useCompactLayout } from "@/shared/ui/useCompactLayout";
import { chromePieceHide, chromePieceOpacity } from "@/shared/ui/compactChrome/chromeCss";
import { useChromePiece } from "@/shared/ui/compactChrome/useCompactChrome";

/**
 * In-flow chrome on compact screens. Desktop renders the children with no box.
 * On compact the row keeps its content height and clips away as `--chrome-offset`
 * consumes the stack above it, one pixel of scroll per pixel of hide.
 */
export function CompactChromeRow({ children }: Readonly<{ children: ReactNode }>) {
  const compact = useCompactLayout();
  const clipRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  useChromePiece(clipRef, innerRef, compact, "top");
  if (!compact) {
    return <Box sx={{ display: "contents" }}>{children}</Box>;
  }
  const hide = chromePieceHide();
  return (
    <Box
      ref={clipRef}
      sx={{
        flexShrink: 0,
        overflow: "hidden",
        height: `calc(var(--chrome-piece-height, 0px) - ${hide})`,
      }}
    >
      <Box
        ref={innerRef}
        sx={{
          height: "max-content",
          width: "100%",
          transform: `translate3d(0, calc(-1 * ${hide}), 0)`,
          opacity: chromePieceOpacity(),
          willChange: "transform, opacity",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Fills the grid slot. The slot itself grows as the chrome rows close. */
export function chromeGridLiftSx() {
  return {
    position: "absolute" as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: "hidden",
  };
}
